import { useCallback, useRef, useState } from "react";

/**
 * Custom hook to manage verification state across components
 * @returns {Object} Object containing verification state and methods to manage it
 */
export const useVerificationState = () => {
  // Create a state to store verification status
  const [verificationState, setVerificationState] = useState({});

  // Create a ref to store verification state that can be accessed by any component
  const verificationRef = useRef(new Map());

  /**
   * Set the verification status for an item
   * @param {string} itemId - ID of the item
   * @param {boolean} isVerified - Verification status
   */
  const setItemVerificationStatus = useCallback((itemId, isVerified) => {
    // Update the ref
    verificationRef.current.set(itemId, isVerified);

    // Update the state for reactivity
    setVerificationState((prevState) => ({
      ...prevState,
      [itemId]: isVerified,
    }));
  }, []);

  /**
   * Check if all items in the given array are verified
   * @param {Array} items - Array of items to check
   * @returns {boolean} True if all items are verified
   */
  const areAllItemsVerified = useCallback((items) => {
    if (!items || items.length === 0) return false;

    return items.every((item) => verificationRef.current.get(item.id) === true);
  }, []);

  /**
   * Check if all items in the verification map are verified
   * @returns {boolean} True if all items in the map are verified, false otherwise
   */
  const isEverythingVerified = useCallback(() => {
    if (verificationRef.current.size === 0) return false;

    // Check if every value in the Map is true
    return Array.from(verificationRef.current.values()).every(
      (isVerified) => isVerified === true
    );
  }, []);

  /**
   * Clear verification status for an item
   * @param {string} itemId - ID of the item to clear
   */
  const clearItemVerification = useCallback((itemId) => {
    verificationRef.current.delete(itemId);

    setVerificationState((prevState) => {
      const newState = { ...prevState };
      delete newState[itemId];
      return newState;
    });
  }, []);

  /**
   * Clear all verification statuses
   */
  const clearAllVerifications = useCallback(() => {
    verificationRef.current.clear();
    setVerificationState({});
  }, []);

  return {
    verificationState,
    verificationRef,
    setItemVerificationStatus,
    areAllItemsVerified,
    isEverythingVerified,
    clearItemVerification,
    clearAllVerifications,
  };
};
